/**
 * The permission catalogue. Roles are bags of these; code always checks a
 * permission, never a role name - so adding a fifth role later needs no
 * code change, only a row in role_permissions.
 */
export const PERMISSIONS = {
  // project management
  'pm.workspace.view': 'pm',
  'pm.workspace.create': 'pm',
  'pm.workspace.manage': 'pm',
  // organizations: list, create and archive them, and place workspaces in them
  'pm.organization.manage': 'pm',
  'pm.board.view': 'pm',
  'pm.board.manage': 'pm',
  'pm.item.create': 'pm',
  'pm.item.update': 'pm',
  'pm.item.delete': 'pm',
  'pm.report.view': 'pm',
  'pm.report.view_all': 'pm',

  // time tracking
  'time.track': 'time',
  'time.view_own': 'time',
  'time.view_team': 'time',
  'time.view_all': 'time',
  'time.adjust': 'time',

  // hr - attendance
  'hr.attendance.self': 'hr',
  'hr.attendance.view_team': 'hr',
  'hr.attendance.view_all': 'hr',
  'hr.attendance.edit': 'hr',

  // hr - leave
  'hr.leave.request': 'hr',
  'hr.leave.view_team': 'hr',
  'hr.leave.view_all': 'hr',
  'hr.leave.approve': 'hr',
  'hr.leave.manage_balances': 'hr',

  // hr - employees
  'hr.employee.view_self': 'hr',
  'hr.employee.view_team': 'hr',
  'hr.employee.view_all': 'hr',
  'hr.employee.manage': 'hr',
  // every department; a department's leads manage its members without it
  'hr.departments.manage': 'hr',

  // hr - configuration
  'hr.settings.manage': 'hr',
  'hr.holidays.manage': 'hr',
  'hr.leave_types.manage': 'hr',

  // administration
  'admin.users.manage': 'admin',
  'admin.roles.view': 'admin',
  'admin.roles.manage': 'admin',
  'admin.audit.view': 'admin',
  'admin.logs.view': 'admin',
} as const;

export type Permission = keyof typeof PERMISSIONS;
export const allPermissions = Object.keys(PERMISSIONS) as Permission[];
export const permissionGroup = (p: Permission) => PERMISSIONS[p];

export const SYSTEM_ROLES = {
  employee: 'employee',
  hr: 'hr',
  admin: 'admin',
  super_admin: 'super_admin',
} as const;
export type SystemRole = keyof typeof SYSTEM_ROLES;

/** Starting grants for the four roles that ship with the system. */
export const SYSTEM_ROLE_PERMISSIONS: Record<SystemRole, Permission[]> = {
  employee: [
    'pm.workspace.view', 'pm.board.view', 'pm.item.create', 'pm.item.update', 'pm.report.view',
    'time.track', 'time.view_own',
    'hr.attendance.self', 'hr.leave.request', 'hr.employee.view_self',
  ],
  hr: [
    'pm.workspace.view', 'pm.board.view', 'pm.report.view',
    'time.track', 'time.view_own', 'time.view_all',
    'hr.attendance.self', 'hr.attendance.view_team', 'hr.attendance.view_all', 'hr.attendance.edit',
    'hr.leave.request', 'hr.leave.view_team', 'hr.leave.view_all', 'hr.leave.approve',
    'hr.leave.manage_balances',
    'hr.employee.view_self', 'hr.employee.view_team', 'hr.employee.view_all', 'hr.employee.manage',
    'hr.departments.manage',
    'hr.settings.manage', 'hr.holidays.manage', 'hr.leave_types.manage',
  ],
  admin: [
    'pm.workspace.view', 'pm.workspace.create', 'pm.workspace.manage',
    'pm.board.view', 'pm.board.manage',
    'pm.item.create', 'pm.item.update', 'pm.item.delete',
    'pm.report.view', 'pm.report.view_all',
    'time.track', 'time.view_own', 'time.view_team', 'time.view_all', 'time.adjust',
    'hr.attendance.self', 'hr.attendance.view_team', 'hr.attendance.view_all',
    'hr.leave.request', 'hr.leave.view_team', 'hr.leave.view_all', 'hr.leave.approve',
    'hr.employee.view_self', 'hr.employee.view_team', 'hr.employee.view_all',
    'hr.departments.manage',
    'admin.users.manage', 'admin.roles.view', 'admin.audit.view', 'admin.logs.view',
  ],
  super_admin: [...allPermissions],
};
